import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppState } from '../context/AppStateContext';
import { StaffProfile } from '../models/staffProfile';
import { UserDataService } from '../services/userDataService';

interface Props {
  selectedTeacherId: number | null;
  onSelectTeacher: (id: number | null) => void;
}

const TRIGGER_SIZE = 82;
const QUICKLIST_SIZE = 56;

// Inline staff avatar — coloured circle with centred initials. Visually
// matches the depth of ChildAvatar's image variant (which is the most common
// state for children on screen) so child and teacher circles read the same.
// The DOM shape mirrors ChildAvatar's initials fallback when no badge is
// shown, so the parent .filter-group__quicklist-item drop-shadow filter
// falls on the same target.
// Inset bottom-darkening + initials text-shadow mirrors .wsg-sdp__circle in
// the session panel, giving the circle a subtle 3-D feel. Matches the same
// treatment applied to ChildAvatar's initials fallback so children and
// teacher circles render with identical depth.
const STAFF_AVATAR_SHADOW = 'inset 0 -2px 4px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.18)';

const staffCircleStyle = (colour: string, size: number): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: '50%',
  backgroundColor: colour,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: Math.max(11, Math.round(size * 0.34)),
  lineHeight: 1,
  boxShadow: STAFF_AVATAR_SHADOW,
  textShadow: '0 1px 1px rgba(0,0,0,0.25)',
  flexShrink: 0,
});

const StaffAvatar: React.FC<{
  staff: StaffProfile;
  size: number;
  showMeBadge?: boolean;
}> = ({ staff, size, showMeBadge }) => {
  const colour = staff.colourCode || '#6c757d';
  const showBadge = showMeBadge && size >= 40;

  // No-badge path: render a single div that mirrors ChildAvatar's initials
  // fallback exactly, so the parent drop-shadow + box-shadow stack identically.
  if (!showBadge) {
    return (
      <div
        role="img"
        aria-label={`${staff.name} avatar`}
        style={staffCircleStyle(colour, size)}
      >
        {staff.initials}
      </div>
    );
  }

  // Badge path — wrapper for absolute-positioning the "ME" pip.
  const badgeSize = Math.max(18, Math.round(size * 0.4));
  const badgeFontSize = Math.max(8, Math.round(badgeSize * 0.45));
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <div
        role="img"
        aria-label={`${staff.name} avatar`}
        style={staffCircleStyle(colour, size)}
      >
        {staff.initials}
      </div>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: badgeSize,
          height: badgeSize,
          borderRadius: '50%',
          backgroundColor: '#fff',
          color: '#26923f',
          fontWeight: 500,
          fontSize: Math.max(8, Math.round(badgeSize * 0.4)),
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #26923f',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
        }}
      >
        me
      </div>
    </div>
  );
};

const ScheduleTeacherFilter: React.FC<Props> = ({ selectedTeacherId, onSelectTeacher }) => {
  const { activeUser } = useAppState();
  const [staff, setStaff] = useState<StaffProfile[]>([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let cancelled = false;
    UserDataService.fetchStaffProfiles({ editorsOnly: true })
      .then(data => { if (!cancelled) setStaff(data); })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch staff for teacher filter', err);
      });
    return () => { cancelled = true; };
  }, []);

  // Close the expanded list whenever the selection changes (mirrors child sidebar).
  useEffect(() => {
    setOpen(false);
  }, [selectedTeacherId]);

  // Outside-click + Escape close.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedTeacher = useMemo(
    () => (selectedTeacherId != null ? staff.find(s => s.id === selectedTeacherId) ?? null : null),
    [staff, selectedTeacherId],
  );

  // Active user pinned to the top of the list (with a "ME" badge). Other
  // staff alphabetical. The trigger button is shown above this list.
  const orderedStaff = useMemo(() => {
    if (!activeUser) {
      return [...staff].sort((a, b) => a.name.localeCompare(b.name));
    }
    const active = staff.find(s => s.id === activeUser.id);
    const others = staff
      .filter(s => s.id !== activeUser.id)
      .sort((a, b) => a.name.localeCompare(b.name));
    return active ? [active, ...others] : others;
  }, [staff, activeUser]);

  if (staff.length === 0) {
    return null;
  }

  // Trigger is always shown (matches child sidebar in schedule mode where
  // showClearAvatar = true). Quicklist collapses when not open.
  const listCollapsed = !open;

  return (
    <aside className="child-sidebar" aria-label="Teacher filter" ref={wrapRef}>
      <section className="filter-group" aria-label="Filter by teacher">
        <div className="filter-group__label">Teachers</div>

        <button
          type="button"
          className={`filter-group__trigger ${open ? 'is-open' : ''}`}
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
          aria-haspopup="true"
          title={selectedTeacher ? selectedTeacher.name : 'All teachers'}
        >
          <div className="filter-group__trigger-avatar">
            {selectedTeacher ? (
              <StaffAvatar
                staff={selectedTeacher}
                size={TRIGGER_SIZE}
                showMeBadge={activeUser?.id === selectedTeacher.id}
              />
            ) : (
              <div className="filter-group__trigger-all" aria-hidden="true">
                <span>All</span>
              </div>
            )}
          </div>
          <span className="filter-group__trigger-hint">
            <i className="fas fa-chevron-down" aria-hidden="true" />
          </span>
        </button>

        <div
          className={`filter-group__quicklist ${listCollapsed ? 'is-collapsed' : ''}`}
          aria-hidden={listCollapsed}
        >
          {/* "All" — clear filter */}
          <div
            className={`filter-group__quicklist-item filter-group__quicklist-item--clear ${selectedTeacher == null ? 'is-selected' : ''}`}
            role="button"
            tabIndex={listCollapsed ? -1 : 0}
            aria-pressed={selectedTeacher == null}
            title="Show all (clear filter)"
            onClick={() => onSelectTeacher(null)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onSelectTeacher(null);
                e.preventDefault();
              }
            }}
          >
            <div className="filter-group__clear-avatar" aria-hidden="true">
              <span className="filter-group__clear-avatar-text">All</span>
            </div>
          </div>

          {orderedStaff.map(s => {
            const isSelected = s.id === selectedTeacherId;
            return (
              <div
                key={s.id}
                className={`filter-group__quicklist-item ${isSelected ? 'is-selected' : ''}`}
                role="button"
                tabIndex={listCollapsed ? -1 : 0}
                aria-pressed={isSelected}
                title={
                  activeUser?.id === s.id ? `Just me — ${s.name}` : s.name
                }
                onClick={() => onSelectTeacher(isSelected ? null : s.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onSelectTeacher(isSelected ? null : s.id);
                    e.preventDefault();
                  }
                }}
              >
                <StaffAvatar
                  staff={s}
                  size={QUICKLIST_SIZE}
                  showMeBadge={activeUser?.id === s.id}
                />
              </div>
            );
          })}
        </div>
      </section>
    </aside>
  );
};

export default ScheduleTeacherFilter;
